import { enumBookingStatus } from '@rematter/frontend-lib-api-client';
import { confirmFileUploadAtom } from '@rematter/modules/customer-portal/_graphql/mutationFileConfirmUpload';
import { atom } from 'jotai';

import { bookingCreateAtom } from '../_graphql/mutationBookingCreate';
import { bookingCreateDraftAtom } from '../_graphql/mutationBookingCreateDraft';
import { bookingFileCreateMutationAtom } from '../_graphql/mutationBookingFileCreate';
import { bookingGetNextDocumentNumberAtom } from '../_graphql/mutationBookingGetNextDocumentNumber';
import { bookingMarkAsOpenAtom } from '../_graphql/mutationBookingMarkAsOpen';
import { bookingUpdateAtom } from '../_graphql/mutationBookingUpdate';
import { bookingUpdateDraftAtom } from '../_graphql/mutationBookingUpdateDraft';
import { fileDeleteMutationAtom } from '../_graphql/mutationFileDelete';
import { setDefaultPhotoRequirementsAtom } from '../_graphql/mutationSetDefaultPhotoRequirements';
import { containerTypesQueryAtom } from '../_graphql/queryContainerTypes';
import { defaultPhotoRequirementsQueryAtom } from '../_graphql/queryDefaultPhotoRequirements';

import { BookingFormState } from '~/modules/trade/types/booking';

const initialState: BookingFormState = {
  id: undefined,
  facilityId: undefined,
  bookingNumber: '',
  status: enumBookingStatus.Draft,
  cutOffDate: undefined,
  earlyReturnDate: undefined,
  vesselArrivalDate: undefined,
  vesselDepartureDate: undefined,
  vesselName: undefined,
  loadingPortId: undefined,
  loadingPort: undefined,
  pickupTerminalId: undefined,
  pickupTerminal: undefined,
  destinationPortId: undefined,
  destinationPort: undefined,
  dropoffTerminalId: undefined,
  dropoffTerminal: undefined,
  steamshipLine: undefined,
  oceanFreight: undefined,
  internalNotes: undefined,
  externalNotes: undefined,
  containers: undefined,
  photoRequirements: undefined,
  files: undefined,
  salesOrders: [],
  salesOrderIds: [],
};

export const $bookingForm = atom<BookingFormState>(initialState);

export const $updateBookingForm = atom(
  null,
  (get, set, update: Partial<BookingFormState>) => {
    const currentState = get($bookingForm);
    set($bookingForm, { ...currentState, ...update });
  },
);

export const $resetBookingForm = atom(null, (get, set) => {
  set($bookingForm, initialState);
});

/// 🟢🟢🟢 REMOTE DATA - Container Types
export const $containerTypes = containerTypesQueryAtom(() => ({
  variables: {},
  skip: false,
}));

/// 🟢🟢🟢 REMOTE DATA - Default Photo requirements
export const $defaultPhotoRequirements = defaultPhotoRequirementsQueryAtom();

// 🟠🟠🟠 ACTIONS - Booking next document number
export const $nextBookingDocumentNumber = bookingGetNextDocumentNumberAtom();

// 🟠🟠🟠 ACTIONS - Set default photo requirements
const $setDefaultPhotoRequirements = setDefaultPhotoRequirementsAtom();

export const $updateDefaultPhotoRequirements = atom(
  null,
  (
    _get,
    set,
    photoRequirements: { description: string; required: boolean }[],
  ) => {
    const filteredRequirements = photoRequirements
      .filter((req) => req.description != null && req.description.trim() !== '')
      .map((photoRequirement, index) => ({
        name: photoRequirement.description,
        isRequired: photoRequirement.required,
        order: index,
      }));

    if (filteredRequirements.length > 0) {
      return set($setDefaultPhotoRequirements, {
        photoRequirements: filteredRequirements,
      });
    }

    return Promise.resolve(undefined);
  },
);

// 🟠🟠🟠 ACTIONS - Open booking
export const $openBooking = bookingMarkAsOpenAtom();

// 🟠🟠🟠 ACTIONS - Create booking
export const $createBooking = bookingCreateAtom();

// 🟠🟠🟠 ACTIONS - Update booking
export const $updateBooking = bookingUpdateAtom();

// 🟠🟠🟠 ACTIONS - Create draft booking
export const $createDraftBooking = bookingCreateDraftAtom();

// 🟠🟠🟠 ACTIONS - Update draft booking
export const $updateDraftBooking = bookingUpdateDraftAtom();

// 🟠🟠🟠 ACTIONS - Create booking file
export const $createBookingFile = bookingFileCreateMutationAtom();

// 🟠🟠🟠 ACTIONS - Confirm booking file upload
export const $confirmBookingFileUpload = confirmFileUploadAtom();

// 🟠🟠🟠 ACTIONS - Delete booking file
export const $deleteBookingFile = fileDeleteMutationAtom();
